import type { Coordinate } from 'ol/coordinate';
import type { EventsKey } from 'ol/events';
import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import OLPoint from 'ol/geom/Point.js';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import { unByKey } from 'ol/Observable';
import Overlay from 'ol/Overlay';
import * as proj from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import type { WatchStopHandle } from 'vue';
import { watchEffect } from 'vue';

import type { TrackPropertiesDTO } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import type { PositionResponse } from '@nora-monorepo/track-api-web-client';
import {
  ConnectivityStatus,
  useGenericAppStore,
  useGenericUserStore,
  useWebViewStore,
} from '@nora-monorepo/ui-shared';

import { MilSymbolStyleManager } from '#nora-ui/map/drawtools/styles/milsymbol-style';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { getLocationFeatureStyle, getOwnLocationFeatureStyle } from '#nora-ui/map/feature-styles/location-feature-style';
import { FeatureStyleManager } from '#nora-ui/map/map/FeatureStyleManager';
import { ClusterableLayer } from '#nora-ui/map/map/layer-managers/ClusterableLayer';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { OpenLayersTrackProperties, TrackProperties } from '#nora-ui/map/types/track.types';
import { TrackVisibilityState } from '#nora-ui/map/types/track.types';
import { useConsentStore } from '#nora-ui/stores/consent-store';

export class TrackLayerManager extends ClusterableLayer {
  readonly LAYER_ID = 'trackLayer';
  readonly LAYER_NAME = 'NORA Tracks';
  override readonly isClickable = true;

  private userStore = useGenericUserStore();
  private webViewStore = useWebViewStore();
  private appStore = useGenericAppStore();
  private consentStore = useConsentStore();

  // Default icon+label styles keyed by call sign, so we don't rebuild them on every render.
  private readonly defaultTrackStyleCache = new Map<string, Style[]>();

  private readonly LINE_WIDTH_DEFAULT = 1;
  private readonly LINE_WIDTH_HIGHLIGHTED = 3;

  protected override LAYER_Z_INDEX = 9999;

  protected override CLUSTER_ICON_NAME = 'person';

  // Separate layer for current user so they're never clustered
  private currentUserSource = new VectorSource();
  private currentUserLayer = new VectorLayer({
    source: this.currentUserSource,
    zIndex: 10000,
    updateWhileAnimating: true,
    style: (feature: FeatureLike) => this.getStyleForSingleFeature(feature),
  });

  // rAF handle so a burst of position updates coalesces into one redraw per frame.
  private pendingRenderHandle: number | null = null;
  private sharingPing?: Overlay;
  private sharingPingMap?: OlMap;
  private sharingPingWatch?: WatchStopHandle;
  private sharingPingListeners: EventsKey[] = [];
  private sharingPingExpiryTimer?: ReturnType<typeof setTimeout>;
  private sharingPingObserver?: IntersectionObserver;
  private sharingPingMapVisible = true;

  constructor(map: NoraMap) {
    super(map);
  }

  override async setup(): Promise<void> {
    const olMap = this.map.getOlMapOrThrow();
    if (this.sharingPingMap === olMap) {
      return;
    }
    this.cleanup();
    await super.setup();
    olMap.addLayer(this.currentUserLayer);
    this.setupPanHandler();
    this.setupSharingPing(olMap);
  }

  /**
   * The ring animates in CSS, independently of the canvas map's render loop.
   * Ownership stays with tracks so marker removal and map teardown also remove it.
   */
  private setupSharingPing(olMap: OlMap): void {
    this.sharingPingMap = olMap;
    if (this.webViewStore.isWebView) {
      return;
    }
    const element = document.createElement('div');
    element.className = 'nora-location-sharing-ping';
    element.setAttribute('aria-hidden', 'true');
    element.hidden = true;
    const ring = document.createElement('div');
    ring.className = 'nora-location-sharing-ring';
    element.appendChild(ring);

    this.sharingPing = new Overlay({
      id: 'own-location-sharing-ping',
      element,
      className: 'nora-location-sharing-overlay',
      positioning: 'center-center',
      stopEvent: false,
    });
    olMap.addOverlay(this.sharingPing);
    this.sharingPingListeners = [
      this.currentUserSource.on('change', this.updateSharingPing),
      this.currentUserLayer.on('change:visible', this.updateSharingPing),
      olMap.on('postrender', this.updateSharingPingVisibility),
    ];
    document.addEventListener('visibilitychange', this.updateSharingPing);
    this.sharingPingMapVisible = true;
    this.sharingPingObserver = new IntersectionObserver((entries) => {
      const entry = entries.at(0);
      if (entry) {
        this.sharingPingMapVisible = entry.isIntersecting;
        this.updateSharingPingVisibility();
      }
    });
    this.sharingPingObserver.observe(olMap.getViewport());
    this.sharingPingWatch = watchEffect(this.updateSharingPing, { flush: 'sync' });
  }

  private updateSharingPing = (): void => {
    clearTimeout(this.sharingPingExpiryTimer);
    this.sharingPingExpiryTimer = undefined;

    if (!this.sharingPing) {
      return;
    }
    const sharing = this.consentStore.getOwnLocationSharingStatus();
    const userId = this.userStore.currentUserId;
    const feature = isNonEmptyString(userId) ? this.currentUserSource.getFeatureById(userId) : null;
    const geometry = feature?.getGeometry();
    const eligible = sharing.active
      && !this.webViewStore.isWebView
      && this.appStore.connectivityStatus === ConnectivityStatus.ONLINE
      && this.currentUserLayer.getVisible();

    const position = eligible && geometry instanceof OLPoint ? geometry.getCoordinates() : undefined;
    this.sharingPing.setPosition(position);
    if (position && sharing.nextExpiry !== undefined && !document.hidden) {
      // Browser timeout delays are signed 32-bit values; recheck distant expiries in chunks.
      this.sharingPingExpiryTimer = setTimeout(
        this.updateSharingPing,
        Math.min(Math.max(0, sharing.nextExpiry - Date.now()), 2_147_483_647),
      );
    }
    this.updateSharingPingVisibility();
  };

  private updateSharingPingVisibility = (): void => {
    const element = this.sharingPing?.getElement();
    if (!element || !this.sharingPingMap) {
      return;
    }
    const position = this.sharingPing?.getPosition();
    const pixel = position ? this.sharingPingMap.getPixelFromCoordinate(position) : null;
    const size = this.sharingPingMap.getSize();
    element.hidden = document.hidden || !this.sharingPingMapVisible
      || pixel === null || size === undefined
      || pixel[0] < 0 || pixel[1] < 0 || pixel[0] > size[0] || pixel[1] > size[1];
  };

  cleanup(): void {
    this.cancelScheduledRender();
    this.sharingPingWatch?.();
    this.sharingPingWatch = undefined;
    unByKey(this.sharingPingListeners);
    this.sharingPingListeners = [];
    document.removeEventListener('visibilitychange', this.updateSharingPing);
    this.sharingPingObserver?.disconnect();
    this.sharingPingObserver = undefined;
    clearTimeout(this.sharingPingExpiryTimer);
    this.sharingPingExpiryTimer = undefined;
    if (this.sharingPing && this.sharingPingMap) {
      this.sharingPingMap.removeOverlay(this.sharingPing);
    }
    this.sharingPing = undefined;
    this.sharingPingMap = undefined;
  }

  /**
   * Load/reload track layer features from last known positions.
   * Clears existing features before loading.
   */
  override load(): void {
    this.unload();

    const lastKnownTracks = this.map.state.track.getAllLastKnownTracks();

    for (const [producerId, track] of Object.entries(lastKnownTracks)) {
      const visibilityState = this.map.state.track.getTrackVisibilityState(producerId);

      if (visibilityState !== TrackVisibilityState.Hidden) {
        this.handleIncomingTrack(track.position, track.properties ?? {});
      }
    }
  }

  /**
   * Unload/clear all track layer features.
   */
  override unload(): void {
    this.cancelScheduledRender();
    this.source.clear();
    this.currentUserSource.clear();
  }

  /**
   * Batches redraws so a burst of incoming updates only triggers one render on the next frame.
   */
  private scheduleRender(): void {
    if (this.pendingRenderHandle !== null) {
      return;
    }

    this.pendingRenderHandle = requestAnimationFrame(() => {
      this.pendingRenderHandle = null;
      this.source.changed();
      this.currentUserSource.changed();
    });
  }

  private cancelScheduledRender(): void {
    if (this.pendingRenderHandle !== null) {
      cancelAnimationFrame(this.pendingRenderHandle);
      this.pendingRenderHandle = null;
    }
  }

  private getVisibleFeatures(features: FeatureLike[]): FeatureLike[] {
    return features.filter(
      (feature: FeatureLike) =>
        feature.get('visibilityState') !== TrackVisibilityState.Hidden,
    );
  }

  protected override getFeatures(feature: FeatureLike): FeatureLike[] {
    const features = feature.get('features') as FeatureLike[] | undefined;

    if (!features) {
      return [];
    }

    return this.getVisibleFeatures(features);
  }

  protected override getStyleForSingleFeature(feature: FeatureLike): Style | Style[] {
    const { visibilityState, callSign, symbolId, symbolPackId, sidc } = getFeatureProperties<TrackProperties>(feature);

    if (visibilityState === TrackVisibilityState.Hidden) {
      return new Style();
    }

    const userId = feature.getId() as string;

    const lastKnownLocation = this.map.state.track.getLastKnownTrackForProducerId(userId);

    if (this.userStore.isCurrentUser(userId)) {
      // Only in the webview, ergo on the device you are sharing location from, show accuracy and heading
      if (this.webViewStore.isWebView) {
        return getOwnLocationFeatureStyle({
          accuracy: {
            show: lastKnownLocation?.position.locationAccuracy != null,
            value: lastKnownLocation?.position.locationAccuracy ?? 0,
          },
          heading: {
            show: lastKnownLocation?.position.heading != null,
            value: lastKnownLocation?.position.heading ?? 0,
          },
        });
      }

      return getOwnLocationFeatureStyle();
    }

    // If the track has a SIDC, attempt to render a milsymbol. If that fails (e.g. invalid SIDC), fall back to regular rendering.
    if (isNonEmptyString(sidc)) {
      const milSymbol = MilSymbolStyleManager.getMilSymbolGraphic({
        sidc,
        heading: lastKnownLocation?.position.heading ?? undefined,
        speed: lastKnownLocation?.position.speed ?? undefined,
        callSign,
      });

      if (milSymbol) {
        return milSymbol;
      }
    }

    const hasCustomSymbol = isNonEmptyString(symbolPackId) && isNonEmptyString(symbolId);

    // Not cached: the symbol image loads async, so a cached placeholder would never update.
    if (hasCustomSymbol) {
      const locationFeatureIcon = getLocationFeatureStyle({
        text: callSign,
        symbolPackId,
        symbolId,
        onLoad: () => this.scheduleRender(),
      });

      return FeatureStyleManager.addStaticLabelWithText(callSign ?? '', locationFeatureIcon);
    }

    return this.getDefaultTrackStyle(callSign ?? '');
  }

  /**
   * Cached default icon + label style for a call sign.
   */
  private getDefaultTrackStyle(callSign: string): Style[] {
    const cached = this.defaultTrackStyleCache.get(callSign);

    if (cached) {
      return cached;
    }

    const locationFeatureIcon = getLocationFeatureStyle({ text: callSign });
    const styles = FeatureStyleManager.addStaticLabelWithText(callSign, locationFeatureIcon);

    this.defaultTrackStyleCache.set(callSign, styles);

    return styles;
  }

  /**
   * Handles incoming track updates.
   */
  handleIncomingTrack(
    position: PositionResponse,
    properties: TrackProperties,
  ): void {
    const producerId = position.producerId;

    const trackIsVisible = this.map.state.track.isTrackVisible(producerId);

    if (!trackIsVisible) {
      return;
    }

    this.updateOrCreateTrackPosition(producerId, position, {
      ...properties,
      visibilityState: TrackVisibilityState.Visible,
    });

    if (this.map.ref.followedProducerId === producerId) {
      this.map.setCenter({
        latitude: position.latitude,
        longitude: position.longitude,
      });
    }
  }

  /**
   * Update or add a track feature on the map.
   */
  private updateOrCreateTrackPosition(
    userId: string,
    position: PositionResponse,
    properties?: TrackProperties,
  ): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);
    const webMercatorCoords = proj.transform(
      [position.longitude, position.latitude],
      MapSetup.WGS84,
      MapSetup.WEB_MERCATOR,
    );

    if (trackFeature) {
      this.updateTrackCoordinates(userId, webMercatorCoords);

      if (properties) {
        trackFeature.setProperties(this.getOpenLayersTrackProperties(properties));
        this.scheduleRender();
      }
    }
    else {
      this.addTrackFeature(userId, webMercatorCoords, properties);
    }
  }

  /**
   * Adds a track feature to the map.
   */
  private addTrackFeature(
    userId: string,
    coordinates: Coordinate,
    properties?: TrackProperties,
  ): void {
    const pointFeature = new Feature({
      id: userId,
      geometry: new OLPoint(coordinates),
    });

    pointFeature.setId(userId);

    if (properties) {
      pointFeature.setProperties(this.getOpenLayersTrackProperties(properties));
    }

    this.getVectorSourceForUser(userId).addFeature(pointFeature);
  }

  /**
   * Updates the coordinates of a track feature.
   */
  private updateTrackCoordinates(userId: string, coordinates: Coordinate): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);

    if (!trackFeature) {
      return;
    }

    const point = trackFeature.getGeometry() as OLPoint;
    point.setCoordinates(
      coordinates,
    );
  }

  /**
   * Gets a track feature by user ID, checking both the current user layer and the clustered layer.
   */
  private getTrackFeatureByUserId(userId: string): Feature | null {
    return this.currentUserSource.getFeatureById(userId)
      ?? this.source.getFeatureById(userId);
  }

  private getVectorSourceForUser(userId: string): VectorSource {
    return this.userStore.isCurrentUser(userId) ? this.currentUserSource : this.source;
  }

  /**
   * Sets the visibility state of a track, removing it from the map if set to hidden.
   */
  setTrackVisibility(
    userId: string,
    visibilityState: TrackVisibilityState,
  ): void {
    const trackFeature = this.getTrackFeatureByUserId(userId);

    if (!trackFeature) {
      const lastKnownLocation = this.map.state.track.getLastKnownTrackForProducerId(userId);

      if (!lastKnownLocation) {
        return;
      }

      if (visibilityState === TrackVisibilityState.Hidden) {
        return;
      }

      this.updateOrCreateTrackPosition(
        userId,
        lastKnownLocation.position,
        {
          ...lastKnownLocation.properties,
          visibilityState,
        },
      );

      return;
    }

    if (visibilityState === TrackVisibilityState.Hidden) {
      this.getVectorSourceForUser(userId).removeFeature(trackFeature);

      return;
    }

    trackFeature.setProperties(
      this.getOpenLayersTrackProperties({
        ...trackFeature.getProperties(),
        visibilityState,
      }),
    );

    this.scheduleRender();
  }

  /**
   * Clears the track for a specific user, optionally filtering by device ID.
   * @param userId
   * @param deviceId
   */
  clearUserTrack(userId: string, deviceId?: string): void {
    if (isNonEmptyString(deviceId)) {
      const lastKnownTrack = this.map.state.track.getLastKnownTrackForProducerId(userId);

      if (lastKnownTrack?.position.properties) {
        const properties: TrackPropertiesDTO = lastKnownTrack.position.properties;

        if (properties.deviceId !== deviceId) {
          return;
        }
      }
    }

    // Realtime updates can leave a producer in either source, so clear both
    // to avoid leaving an own-user marker behind after a source transition.
    for (const source of [this.currentUserSource, this.source]) {
      const trackFeature = source.getFeatureById(userId);

      if (trackFeature) {
        source.removeFeature(trackFeature);
      }
    }
  }

  private setupPanHandler() {
    const olMap = this.map.getOlMapOrThrow();

    olMap.on('movestart', () => {
      const isAnimating = olMap.getView().getAnimating();

      if (isAnimating) {
        return;
      }

      this.map.ref.stopFollowing();
      this.map.ref.setHeadingUpMode(false);
    });
  }

  /**
   * Gets the OpenLayers track properties from the generic track properties.
   */
  private getOpenLayersTrackProperties(trackProperties: TrackProperties): OpenLayersTrackProperties {
    const lineWidth = trackProperties.visibilityState === TrackVisibilityState.Highlighted ? this.LINE_WIDTH_HIGHLIGHTED : this.LINE_WIDTH_DEFAULT;

    const formattedTrack: OpenLayersTrackProperties = {
      ...trackProperties,
      fill_color: 'blue',
      line_width: lineWidth,
      label_text: trackProperties.callSign ?? '',
    };

    return formattedTrack;
  }
}
